import { toast } from '@3sc/common-component';
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, styled, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';
import { ArrowDown, ArrowUp } from '../../../assets/svg-icons';
import { activateSNOPMessage, activateSNOEMessage, APPBAR_MENU, ENABLE_FOR_MENU } from '../../../constant/constant';
import { accessibleMenus, getActiveMenu, redirectToPath } from '../../../helper/com-helper';
import { getLocalStorageItem } from '../../../helper/localStorageHelper';
import useCustomEvent from '../../../hooks/useCustomEvent';
import { ENUM_EVENT } from '../../../utils/eventEmitter';
import MenuPopOver from '../menuPopOver';
import CustomTooltip from "../../customTooltip"

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
    color: theme.palette.primary.textColor, // Default icon color
}));

const ListItemButtonStyled = styled(ListItemButton)(({ theme, open }) => ({
    padding: 0,
    position: 'relative',
    '&:hover': {
        '& .MuiTypography-root': {
            color: theme.palette.primary.activeText,
        },
    },
}));

const MenuItems = ({ menuData, open, isChild = false, isPopOver = false, selectedMenu, onNavigate }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = useState(isPopOver && !isChild ? { [menuData[0].name]: true } : {});
    const [snop, setSnop] = useState(getLocalStorageItem("snop") ?? {});
    const [activatedSnoe, setActivatedSnoe] = useState(getLocalStorageItem("snoe") ?? {});

    const syncActivationState = () => {
        setSnop(getLocalStorageItem("snop") ?? {});
        setActivatedSnoe(getLocalStorageItem("snoe") ?? {});
    };

    useCustomEvent(ENUM_EVENT.SN_ACTIVATE, () => {
        syncActivationState();
    });

    useCustomEvent(ENUM_EVENT.SNOE_ACTIVATE, () => {
        syncActivationState();
    })

    const [popOverData, setPopOverData] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (!open)
            setExpandedMenus({});
    }, [open])

    useEffect(() => {
        syncActivationState();
    }, [open, selectedMenu?.parent, location.pathname]);

    useEffect(() => {
        const handleStorageOrFocus = () => {
            syncActivationState();
        };

        window.addEventListener("storage", handleStorageOrFocus);
        window.addEventListener("focus", handleStorageOrFocus);

        return () => {
            window.removeEventListener("storage", handleStorageOrFocus);
            window.removeEventListener("focus", handleStorageOrFocus);
        };
    }, []);

    const toggleMenu = (menu) => {
        if (menu?.child) {
            setExpandedMenus((prev) => ({
                ...prev,
                [menu?.name]: !prev[menu?.name],
            }));
        } else {
            redirectToPath(menu);
            onNavigate?.();
        }
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openPopOver = (e, menu) => {
        if (!!menu?.child) {
            setAnchorEl(e.currentTarget);
            setPopOverData([menu])
        } else {
            redirectToPath(menu)
        }
    }

    const onButtonClick = (e, item) => {
        const isMenuDisabled = checkIfMenuDisabled(item, selectedMenu);

        if (isMenuDisabled) {
            const message = selectedMenu?.parent === APPBAR_MENU?.IOP ? t("activateSNOEMessage") : (t("activateSNOPMessage"));
            toast.error(message);
            return;
        }
        if (!open) {
            openPopOver(e, item)
        } else {
            toggleMenu(item)
        }
    }

    const checkIfMenuDisabled = (item, selectedMenu) => {
        const latestSnop = getLocalStorageItem("snop") ?? snop ?? {};
        const latestActivatedSnoe = getLocalStorageItem("snoe") ?? activatedSnoe ?? {};

        switch (selectedMenu?.parent) {
            case APPBAR_MENU?.IBP:
                return !ENABLE_FOR_MENU.includes(item?.name) && isEmpty(latestSnop)
            case APPBAR_MENU?.IOP:
                return !ENABLE_FOR_MENU.includes(item?.name) && isEmpty(latestActivatedSnoe)
            default:
                return false;
        }
    };

    return (
        <List sx={{
            mb: "0rem",
            py: isChild || isPopOver ? "0rem" : "0.8rem",
            maxHeight: isChild ? "50vh" : "90vh",
            overflowY: "auto",
            overflowX: "hidden"
        }}>
            {menuData?.map((item, index) => {
                const isExpanded = expandedMenus[item?.name] || false;
                const MenuIcon = item?.icon;
                const isMenuActive = getActiveMenu({ item, path: location.pathname });
                const isMenuDisabled = checkIfMenuDisabled(item, selectedMenu);
                return (
                    <Fragment key={`${item?.name}${index}`}>
                        <ListItem
                            disablePadding
                            position="relative"
                            sx={{
                                display: "block",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: "50%",
                                    transform: `translateY(${open ? "-50%" : "-70%"})`,
                                    height: open ? "60%" : "40%", // Adjust heights for open and closed states
                                    width: "0.15rem", // Border thickness
                                    borderTopRightRadius: "0.3rem",
                                    borderBottomRightRadius: "0.3rem",
                                    backgroundColor: isMenuActive && !isExpanded && item?.icon
                                        ? theme.palette.primary.activeBorderColor
                                        : "transparent",
                                },
                            }}
                        >
                            <ListItemButtonStyled
                                onClick={(e) => onButtonClick(e, item)}
                                sx={{
                                    opacity: isMenuDisabled ? 0.5 : 1,
                                    minHeight: isChild ? "2.8rem" : "3.5rem",
                                    justifyContent: open ? "initial" : "center",
                                    pr: "1.4rem",
                                    ml: !item?.icon && isChild ? "2rem" : "0rem",
                                    mr: !item?.icon && isChild ? "1.8rem" : "0rem",
                                    pl: isChild ? item?.icon ? "1.8rem" : "1.4rem" : "1rem",
                                    borderRight: isChild && !item?.icon ? `0.1rem solid ${theme.palette.primary.borderColor}` : "none",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: "50%",
                                        transform: `translateY(${open ? "-50%" : "-70%"})`,
                                        height: open ? "60%" : "40%", // Adjust heights for open and closed states
                                        width: "0.15rem", // Border thickness
                                        borderTopRightRadius: "0.3rem",
                                        borderBottomRightRadius: "0.3rem",
                                        backgroundColor: isMenuActive && (!item?.child || !isExpanded)
                                            ? theme.palette.primary.activeBorderColor
                                            : "transparent",
                                    },
                                }}
                            >
                                <Stack
                                    direction={open ? "row" : "column"}
                                    justifyContent={"center"}
                                    alignContent={"center"}
                                    sx={{
                                        my: open ? "none" : "0.8rem",
                                        textAlign: "center"
                                    }}
                                >
                                    <CustomTooltip
                                        title={open ? "" : t(item?.name)}
                                        arrow
                                        placement="right"
                                    >
                                        {item?.icon && (
                                            <ListItemIconStyled
                                                sx={{
                                                    minWidth: "3rem",
                                                    mr: open ? "1rem" : "none",
                                                    justifyContent: "center",
                                                    color: isExpanded
                                                        ? theme.palette.primary.activeText
                                                        : theme.palette.primary.textColor, // Default icon color
                                                }}
                                            >
                                                <MenuIcon color={theme.palette.primary.textColor} />
                                            </ListItemIconStyled>
                                        )}
                                        {!open && (
                                            <ListItemText
                                                primary={t(item?.shortName)}
                                                sx={{
                                                    transition: "color 0.3s ease",
                                                    mt: "0rem",
                                                    whiteSpace: "break-spaces",
                                                    wordBreak: "break-word",
                                                }}
                                                primaryTypographyProps={{
                                                    fontSize: open ? "1.1rem" : "0.7rem", // Set your desired font size
                                                    color: isMenuActive && (!item?.child || !isExpanded)
                                                        ? theme.palette.primary.activeText
                                                        : theme.palette.primary.textColor, // Optional: Add color here
                                                }}
                                            />
                                        )}
                                    </CustomTooltip>
                                </Stack>

                                <ListItemText
                                    primary={t(item?.name)}
                                    sx={{
                                        transition: "color 0.3s ease",
                                        opacity: open ? 1 : 0,
                                        whiteSpace: open ? "break-spaces" : "nowrap",
                                        wordBreak: "break-word",
                                    }}
                                    primaryTypographyProps={{
                                        fontSize: "1.1rem",
                                        color: isMenuActive
                                            ? theme.palette.primary.activeText
                                            : theme.palette.primary.textColor,
                                    }}
                                />
                                {item?.child && open && (
                                    <ListItemIconStyled
                                        sx={{
                                            minWidth: 0,
                                            justifyContent: "center",
                                            color: isExpanded
                                                ? theme.palette.primary.activeText
                                                : theme.palette.primary.textColor, // Default icon color
                                        }}
                                    >
                                        {isExpanded ? (
                                            <>
                                                <ArrowUp color={theme.palette.primary.textColor} />
                                            </>

                                        ) : (
                                            <ArrowDown color={theme.palette.primary.textColor} />
                                        )}
                                    </ListItemIconStyled>
                                )}
                            </ListItemButtonStyled>
                        </ListItem>
                        {!!item?.child && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <MenuItems
                                    menuData={accessibleMenus(Object?.values(item?.child))}
                                    open={open}
                                    isChild={true}
                                    isPopOver={isPopOver}
                                    onNavigate={onNavigate}
                                />
                            </Collapse>
                        )
                        }
                    </Fragment>
                );
            })}
            {/* Popover */}
            {
                !open && (
                    <MenuPopOver
                        handleClose={handleClose}
                        menuData={popOverData}
                        anchorEl={anchorEl}
                    />
                )
            }
        </List>
    );
}

MenuItems.propTypes = {
    menuData: PropTypes.array,
    open: PropTypes.bool,
    isChild: PropTypes.bool,
    isPopOver: PropTypes.bool,
    selectedMenu: PropTypes.object,
    onNavigate: PropTypes.func,
};

export default MenuItems
